import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useNavigate } from "react-router-dom";
import { FaClock, FaBook, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import "./AllExams.css";
import { getStudentSubjects } from "../../redux/slices/subjectsSlice";
import { fetchExamsBySubject, setCurrentSubject, setCurrentPage } from "../../redux/slices/examsSlice";
import { startExamSession } from '../../redux/slices/examSessionSlice';
import { getTeacherClasses } from "../../redux/slices/lessonsSlice";
import { fetchTeacherClassTests, setCurrentClassId, setCurrentPage as setTeacherTestsPage } from "../../redux/slices/teacherTestsSlice";
import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteTestButton from "../../components/AddLessonModal/DeleteTestButton";

function AllExams() {
    const { isDarkMode } = useDarkMode();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.auth);
    const { studentSubjects } = useSelector((state) => state.subjects);
    const { exams, loading, error, pagination, currentSubject, currentPage } = useSelector((state) => state.exams);
    const { teacherClasses } = useSelector((state) => state.lessons);
    const { tests: teacherTests, loading: teacherTestsLoading, error: teacherTestsError, pagination: teacherTestsPagination, currentClassId, currentPage: teacherTestsCurrentPage } = useSelector((state) => state.teacherTests);
    
    const [activeTab, setActiveTab] = useState("الكل");
    const [selectedClassId, setSelectedClassId] = useState(null);

    const isTeacher = currentUser?.role === 'teacher';

    useEffect(() => {
        window.scrollTo(0, 0);
        
        if (isTeacher) {
            // For teachers, fetch their classes
            dispatch(getTeacherClasses())
                .catch((error) => {
                    console.error('Error fetching teacher classes:', error);
                    toast.error("فشل في تحميل الصفوف");
                });
        } else {
            // For students, fetch subjects
            dispatch(getStudentSubjects())
                .catch((error) => {
                    console.error('Error fetching student subjects:', error);
                    toast.error("فشل في تحميل المواد");
                });
        }
    }, [dispatch, isTeacher]);

    // When teacher classes are loaded, select the first class and fetch its tests
    useEffect(() => {
        if (isTeacher && teacherClasses.length > 0 && !selectedClassId) {
            const firstClass = teacherClasses[0];
            setActiveTab(firstClass.name);
            setSelectedClassId(firstClass.id);
            dispatch(setCurrentClassId(firstClass.id));
            dispatch(setTeacherTestsPage(1));
            dispatch(fetchTeacherClassTests({ classId: firstClass.id, page: 1 }))
                .catch((error) => {
                    console.error('Error fetching initial teacher tests:', error);
                    toast.error("فشل في تحميل اختبارات الصف الأول");
                });
        }
    }, [isTeacher, teacherClasses, selectedClassId, dispatch]);

    // When student subjects are loaded, fetch exams for "الكل"
    useEffect(() => {
        if (!isTeacher && studentSubjects.length > 0) {
            dispatch(fetchExamsBySubject({ subjectId: 0, page: 1 }))
                .catch((error) => {
                    console.error('Error fetching initial exams:', error);
                    toast.error("فشل في تحميل الاختبارات الأولية");
                });
        }
    }, [isTeacher, studentSubjects, dispatch]);

    useEffect(() => {
        const token = localStorage.getItem("token3almny");
        if (!token) {
            navigate("/login");
        }
    }, [navigate]);

    // بناء التابس بناءً على نوع المستخدم
    const getTabs = () => {
        if (isTeacher) {
            return teacherClasses.map((classItem) => ({ name: classItem.name, id: classItem.id }));
        } else {
            return [
                { name: "الكل", id: 0 },
                ...studentSubjects.map((subject) => ({ name: subject.title, id: subject.id }))
            ];
        }
    };

    const tabs = getTabs();

    // عند تغيير التاب
    useEffect(() => {
        if (isTeacher) {
            // For teachers, handle class tabs
            const selectedClass = tabs.find((s) => s.name === activeTab);
            if (selectedClass) {
                setSelectedClassId(selectedClass.id);
                dispatch(setCurrentClassId(selectedClass.id));
                dispatch(setTeacherTestsPage(1));
                dispatch(fetchTeacherClassTests({ classId: selectedClass.id, page: 1 }))
                    .catch((error) => {
                        console.error('Error fetching teacher tests:', error);
                        toast.error("فشل في تحميل اختبارات الصف");
                    });
            }
        } else {
            // For students, handle subject tabs
            const selectedSubject = tabs.find((s) => s.name === activeTab);
            if (selectedSubject) {
                dispatch(setCurrentSubject(selectedSubject.id));
                dispatch(setCurrentPage(1));
                dispatch(fetchExamsBySubject({ subjectId: selectedSubject.id, page: 1 }))
                    .catch((error) => {
                        console.error('Error fetching exams:', error);
                        toast.error("فشل في تحميل الاختبارات");
                    });
            }
        }
        // eslint-disable-next-line
    }, [activeTab, isTeacher]);

    // عند تغيير الصفحة
    const handlePageChange = (page) => {
        if (isTeacher) {
            dispatch(setTeacherTestsPage(page));
            if (selectedClassId) {
                dispatch(fetchTeacherClassTests({ classId: selectedClassId, page }))
                    .catch((error) => {
                        console.error('Error fetching teacher tests:', error);
                        toast.error("فشل في تحميل اختبارات الصف");
                    });
            }
        } else {
            dispatch(setCurrentPage(page));
            const selectedSubject = tabs.find((s) => s.name === activeTab);
            dispatch(fetchExamsBySubject({ subjectId: selectedSubject.id, page }))
                .catch((error) => {
                    console.error('Error fetching exams:', error);
                    toast.error("فشل في تحميل الاختبارات");
                });
        }
    };

    // Handle test actions for teachers
    const handleEditTest = (testId) => {
        toast.info("ميزة تعديل الاختبار قيد التطوير");
    };

    const handleDeleteTest = (testId) => {
        // سيتم التعامل مع الحذف من خلال مكون DeleteTestButton
    };

    const handleDeleteTestSuccess = () => {
        // إعادة تحميل الاختبارات بعد الحذف
        if (selectedClassId) {
            dispatch(fetchTeacherClassTests({ classId: selectedClassId, page: currentPageNumber }))
                .catch((error) => {
                    console.error('Error refreshing teacher tests:', error);
                    toast.error("فشل في إعادة تحميل الاختبارات");
                });
        }
    };

    const handleCreateTest = () => {
        navigate('/create-test');
    };

    // Get current data based on user type
    const currentData = isTeacher ? teacherTests : exams;
    const currentLoading = isTeacher ? teacherTestsLoading : loading;
    const currentError = isTeacher ? teacherTestsError : error;
    const currentPagination = isTeacher ? teacherTestsPagination : pagination;
    const currentPageNumber = isTeacher ? teacherTestsCurrentPage : currentPage;

    return (
        <div className={`questions-section ${isDarkMode ? "dark-mode" : "light-mode"}`}>
            <ToastContainer />
            <div className="container">
                {/* Breadcrumb */}
                <div className="lessons-breadcrumb">
                    <h1 className="breadcrumb-title">الاختبارات</h1>
                    <div className="breadcrumb-links2">
                        <span className="breadcrumb-link">الرئيسية</span>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-current">الاختبارات</span>
                    </div>
                </div>

                {/* Tabs */}
                <div className="lessons-tabs">
                    <div className="tabs-header">
                        <span className="subjects-text">{isTeacher ? "الصفوف" : "المواد"}</span>
                        <div className="tabs-container">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    className={`tab-button ${activeTab === tab.name ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.name)}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </div>
                        {/* Create Test Button for Teachers */}
                        {isTeacher && (
                            <button
                                className="create-test-btn my-4"
                                onClick={handleCreateTest}
                                style={{
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: '2px solid #007bff',
                                    borderRadius: '8px',
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#0056b3';
                                    e.target.style.borderColor = '#0056b3';
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#007bff';
                                    e.target.style.borderColor = '#007bff';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 2px 8px rgba(0, 123, 255, 0.3)';
                                }}
                            >
                                <FaPlus />
                                إنشاء اختبار جديد
                            </button>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {currentLoading && (
                    <div className="text-center my-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">جاري التحميل...</span>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {currentError && (
                    <div className="text-center my-4">
                        <div className="alert alert-danger" role="alert">
                            {currentError}
                        </div>
                    </div>
                )}

                {/* Cards */}
                {!currentLoading && !currentError && (
                    <div className="exam-cards-grid">
                        {currentData.length > 0 ? currentData.map((item) => (
                            <div className="exam-card" key={item.id}>
                                <div className="exam-image-container">
                                    <img
                                        src={item.subject_image || "/courseimage.png"}
                                        alt="Exam"
                                        className="exam-image"
                                    />
                                </div>
                                
                                {/* Status Badge */}
                                <span className={`exam-status ${isTeacher ? 'active' : 'completed'}`}>
                                    {isTeacher ? 'نشط' : 'جاهز'}
                                </span>
                                
                                {/* Content */}
                                <div className="exam-content">
                                    <h3 className="exam-title">{item.title}</h3>
                                    
                                    <div className="exam-time">
                                        <FaClock className="clock-icon" />
                                        <span>{item.duration_time || item.time}</span>
                                    </div>
                                    
                                    {/* Additional details for teacher tests */}
                                    {isTeacher && (
                                        <>
                                            <div className="exam-date">
                                                <span>التاريخ: {item.day}</span>
                                            </div>
                                            <div className="exam-start-time">
                                                <span>وقت البدء: {item.start_time}</span>
                                            </div>
                                        </>
                                    )}
                                    
                                    <div className="exam-footer">
                                        {!isTeacher && (
                                            <div className="exam-teacher">
                                                <img src="/user.png" style={{ width: '40px', height: '40px' }} alt="Teacher" />
                                                <span>{item.teacher_name}</span>
                                            </div>
                                        )}
                                        <div className="exam-subject">
                                            <FaBook className="book-icon" />
                                            <span>{item.subject_name || item.subject_title}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Buttons */}
                                    {isTeacher ? (
                                        // Teacher action buttons
                                        <div className="exam-actions">
                                            <DeleteTestButton
                                                testId={item.id}
                                                testTitle={item.title}
                                                onDeleteSuccess={handleDeleteTestSuccess}
                                            />
                                        </div>
                                    ) : (
                                        // Student start exam button
                                        <button
                                            type="button"
                                            className="exam-btn start"
                                            style={{ zIndex: 9999, position: "relative", pointerEvents: "auto" }}
                                            onClick={async () => {
                                                const res = await dispatch(startExamSession(item.id));
                                                if (res.meta.requestStatus === "fulfilled") {
                                                    // حفظ مدة الامتحان في localStorage
                                                    if (res.payload && res.payload.result && res.payload.result.duration_time) {
                                                        let duration = res.payload.result.duration_time;
                                                        // تحويل إلى ثواني مع اعتبار "HH:MM" و"MM:SS"
                                                        const parts = duration.split(":").map(Number);
                                                        let seconds = 0;
                                                        if (parts.length === 3) {
                                                            seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
                                                        } else if (parts.length === 2) {
                                                            if (parts[0] >= 60) {
                                                                seconds = parts[0] * 60 + parts[1]; // MM:SS
                                                            } else {
                                                                seconds = parts[0] * 3600 + parts[1] * 60; // HH:MM
                                                            }
                                                        } else if (parts.length === 1) {
                                                            seconds = parts[0] * 60;
                                                        }
                                                        if (seconds < 300) { // أقل من 5 دقائق
                                                            seconds = 1800; // 30 دقيقة
                                                        }
                                                        localStorage.setItem("exam_duration_seconds", seconds);
                                                    }
                                                    toast.success("تم بدء الامتحان بنجاح!");
                                                    setTimeout(() => navigate(`/startExam/${item.id}`), 1000);
                                                } else {
                                                    toast.error(res.payload || "فشل بدء الامتحان");
                                                }
                                            }}
                                        >
                                            ▶ بدء الاختبار
                                        </button>
                                    )}
                                </div>
                            </div>
                        )) : (
                            <div className="text-center my-4">
                                <p>لا توجد اختبارات متاحة</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Pagination */}
                {currentPagination && (
                    <div className="pagination-container mt-4">
                        <div className="pagination">
                            {Array.from({ length: currentPagination.pages_count || 1 }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    className={`pagination-btn ${currentPageNumber === page ? 'active' : ''}`}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AllExams;
